import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import useApi from "../../hooks/useApi";

type SearchVariant = "internal" | "external";

interface Props {
  /** internal = full /patients (name/DOB/MRN); external = assigned-only /external/patients (name). */
  variant?: SearchVariant;
  /** Style for the coloured AppBar (translucent/light) vs a light surface like a popover. */
  onDark?: boolean;
  /** Focus the input when mounted (e.g. inside a mobile popover). */
  focusOnMount?: boolean;
  /** Called after a result is chosen (e.g. to close a mobile popover). */
  onSelect?: () => void;
}

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

interface SearchResult {
  id: string;
  primary: string;
  secondary?: string;
}

// A search "context" is what the header quick-search targets. It is page-aware: on the users list it
// searches users, on the imaging-centers list it searches imaging centers, and everywhere else it
// searches patients. Each context knows its endpoint, how to label a row, and where selecting a row
// navigates. All endpoints are ListModel { data, count } and take a `q` term (see the *Parameters
// classes).
interface SearchContext {
  key: string;
  endpoint: string;
  /**
   * AUD-21. The patient contexts POST `{endpoint}/search` instead of appending `?q=`, because here
   * the term IS the PHI: `q` matches a patient's name, MRN or date of birth, and this field fires a
   * request every 300 ms as the user types. On a GET, every one of those keystroke-prefixes of a
   * patient's name would be written to the ALB access log, CloudFront's log and browser history —
   * the highest-volume instance of the finding, and none of it inside the audited PHI perimeter.
   *
   * The users and imaging-centre contexts stay on GET: a staff directory and a facility list are
   * not patient records.
   */
  searchMethod: "get" | "post";
  placeholder: string;
  noOptionsText: string;
  toNav: (id: string) => string;
  normalize: (raw: Record<string, unknown>) => SearchResult;
}

const formatDob = (dob?: unknown) => {
  if (!dob || (typeof dob !== "string" && !(dob instanceof Date))) return "";
  const d = dayjs(dob as string | Date);
  return d.isValid() ? d.format("M/D/YYYY") : "";
};

const str = (v: unknown) => (v == null ? "" : String(v));

const patientContext = (external: boolean): SearchContext => ({
  key: external ? "patients-external" : "patients",
  endpoint: external ? "external/patients" : "patients",
  searchMethod: "post",
  placeholder: "Search patients…",
  noOptionsText: "No patients found",
  toNav: (id) => `/patients/${id}`,
  normalize: (raw) => {
    const name = `${str(raw.firstName)} ${str(raw.lastName)}`.trim();
    const dob = formatDob(raw.dob);
    const mrn = raw.mrn != null ? `MRN ${raw.mrn}` : "";
    const secondary = [dob && `DOB ${dob}`, mrn].filter(Boolean).join(" · ");
    return {
      id: str(raw.id ?? raw.patientId),
      primary: name || "(no name)",
      secondary: secondary || undefined,
    };
  },
});

const usersContext: SearchContext = {
  key: "users",
  endpoint: "users",
  searchMethod: "get",
  placeholder: "Search users…",
  noOptionsText: "No users found",
  toNav: (id) => `/user/${id}`,
  normalize: (raw) => ({
    id: str(raw.id),
    primary: `${str(raw.firstName)} ${str(raw.lastName)}`.trim() || "(no name)",
    secondary: str(raw.email) || undefined,
  }),
};

const imagingCentersContext: SearchContext = {
  key: "imaging-centers",
  endpoint: "imaging-centers",
  searchMethod: "get",
  placeholder: "Search imaging centers…",
  noOptionsText: "No imaging centers found",
  toNav: (id) => `/imaging-centers/${id}`,
  normalize: (raw) => ({
    id: str(raw.id),
    primary: str(raw.name) || "(no name)",
    secondary: str(raw.city) || undefined,
  }),
};

const resolveContext = (
  pathname: string,
  variant: SearchVariant,
): SearchContext => {
  // External providers only ever search their assigned patients (they have no users/imaging pages).
  if (variant === "external") return patientContext(true);
  if (pathname.startsWith("/users")) return usersContext;
  if (pathname.startsWith("/imaging-centers")) return imagingCentersContext;
  return patientContext(false);
};

/**
 * Global quick-search for the app header. Page-aware: it searches whatever the current list is about
 * (patients, users, imaging centers). Debounced, server-side, access-scoped search; selecting a
 * result navigates to that record. ⌘/Ctrl+K focuses the field.
 */
export default function PatientSearchBar({
  variant = "internal",
  onDark = true,
  focusOnMount = false,
  onSelect,
}: Props) {
  const api = useApi();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  const context = useMemo(
    () => resolveContext(pathname, variant),
    [pathname, variant],
  );

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const requestSeq = useRef(0);

  const focusInput = () => containerRef.current?.querySelector("input")?.focus();
  const blurInput = () =>
    (containerRef.current?.querySelector("input") as HTMLInputElement | null)?.blur();

  // Clear stale results when the search context changes (e.g. navigating from users to patients).
  useEffect(() => {
    setInputValue("");
    setOptions([]);
  }, [context.key]);

  // Debounced search; a monotonic sequence guards against out-of-order responses.
  useEffect(() => {
    const term = inputValue.trim();
    if (term.length < MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const seq = ++requestSeq.current;
    const timer = setTimeout(async () => {
      try {
        const query = { q: term, pageNumber: 0, pageSize: 8 };
        const res =
          context.searchMethod === "post"
            ? await api.post(`${context.endpoint}/search`, query)
            : await api.get(context.endpoint, { params: query });
        const rows: Record<string, unknown>[] = res.data?.data ?? [];
        if (seq === requestSeq.current) setOptions(rows.map(context.normalize));
      } catch (error) {
        // A failed search is not "no results". Swallowing it to [] made an outage look like an empty
        // result set: the user retypes, still gets nothing, and concludes the record is not there.
        console.error("Quick search failed", error);
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue, api, context]);

  // ⌘/Ctrl+K to focus the search from anywhere.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        focusInput();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Focus when embedded (e.g. mobile popover) that mounts on demand.
  useEffect(() => {
    if (focusOnMount) focusInput();
  }, [focusOnMount]);

  const darkFieldSx = {
    "& .MuiOutlinedInput-root": {
      color: "inherit",
      backgroundColor: (theme: Theme) => alpha(theme.palette.common.white, 0.15),
      borderRadius: 2,
      "&:hover": { backgroundColor: (theme: Theme) => alpha(theme.palette.common.white, 0.25) },
      "& fieldset": { borderColor: "transparent" },
      "&:hover fieldset": { borderColor: "transparent" },
      "&.Mui-focused fieldset": {
        borderColor: (theme: Theme) => alpha(theme.palette.common.white, 0.5),
      },
    },
    "& .MuiInputBase-input::placeholder": { color: "inherit", opacity: 0.8 },
  } as const;

  return (
    <Box ref={containerRef} sx={{ width: onDark ? { xs: 200, sm: 280, md: 380 } : "100%" }}>
      <Autocomplete<SearchResult>
        size="small"
        fullWidth
        open={open && inputValue.trim().length >= MIN_CHARS}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        loading={loading}
        // Server already filtered — don't re-filter client-side.
        filterOptions={(x) => x}
        inputValue={inputValue}
        onInputChange={(_, value, reason) => {
          if (reason !== "reset") setInputValue(value);
        }}
        getOptionLabel={(option) => option.primary}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, value) => {
          if (value) {
            navigate(context.toNav(value.id));
            setInputValue("");
            setOptions([]);
            setOpen(false);
            blurInput();
            onSelect?.();
          }
        }}
        noOptionsText={context.noOptionsText}
        clearOnBlur
        blurOnSelect
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.primary}
              </Typography>
              {option.secondary && (
                <Typography variant="caption" color="text.secondary">
                  {option.secondary}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={context.placeholder}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "inherit", opacity: 0.9 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.slotProps.input?.endAdornment}
                  </>
                ),
              },
            }}
            sx={onDark ? darkFieldSx : undefined}
          />
        )}
      />
    </Box>
  );
}
